import { type FC, useEffect, useState } from "react"
import styled from "styled-components"

import { parseNumber } from "@forento/core/utilities/number"
import { toggleStateArray } from "@forento/ui/utilities/array"

import Button, { SubmitButton } from "~/components/Button"
import Checkbox from "~/components/Checkbox"
import Dropdown from "~/components/Dropdown"
import InputLabel from "~/components/InputLabel"
import { DefaultModal, ModalButtons, ModalContentPadding, ModalTitle } from "~/components/Modal"
import { usePlatform } from "~/contexts/UserContext"
import routes from "~/utilities/routes"
import trpc, { query } from "~/utilities/trpc"

import Uploader from "./Uploader"

type Props = { isOpen: boolean; onClose(): void }

const InviteUsersModal: FC<Props> = ({ isOpen, onClose }) => {
	const platform = usePlatform()

	const [emails, setEmails] = useState<string[]>([])
	const [membershipId, setMembershipId] = useState<number>()
	const [courseIds, setCourseIds] = useState<number[]>([])
	const [isSubmitting, setSubmitting] = useState(false)

	const memberships = query.membership.list.useQuery(undefined, { enabled: isOpen })
	const courses = query.course.list.useQuery(undefined, { enabled: isOpen })

	useEffect(() => {
		if (memberships.data === undefined) {
			setMembershipId(undefined)
		}
	}, [membershipId, memberships.data])

	const isInputValid = emails.length > 0

	const handleSubmit = async () => {
		if (!isInputValid) return

		setSubmitting(true)
		try {
			await trpc.platform.inviteUsers.mutate({ emails, membershipId, courseIds })
			onClose()
			setEmails([])
			setCourseIds([])
		} catch (error) {
			console.error(error)
			setSubmitting(false)
			return Promise.reject(error)
		}
		setSubmitting(false)
	}

	if (!platform!.customEmailSender?.isVerified) {
		return (
			<DefaultModal onClose={onClose} isOpen={isOpen} onSubmit={handleSubmit}>
				<ModalTitle>Invite users</ModalTitle>
				<Content>
					<p>You need to connect and verify a custom sender email domain in order to invite users.</p>
				</Content>
				<ModalButtons>
					<Button variant="secondary" onClick={onClose}>
						Close
					</Button>
					<Button variant="primary" onClick={routes.settings.customizeAcademy()}>
						Settings
					</Button>
				</ModalButtons>
			</DefaultModal>
		)
	}

	return (
		<DefaultModal onClose={onClose} isOpen={isOpen} onSubmit={handleSubmit}>
			<ModalTitle>Invite users</ModalTitle>
			<Content>
				{emails.length === 0 ? (
					<Uploader onEmailsUploaded={setEmails} />
				) : (
					<List>
						{emails.map(email => (
							<li key={email}>{email}</li>
						))}
					</List>
				)}
				{memberships.data !== undefined && memberships.data.length > 0 && (
					<div>
						<InputLabel>Default membership</InputLabel>
						<Dropdown
							items={[
								{ id: "none", title: "No default membership" },
								...memberships.data.map(membership => ({
									id: membership.id.toString(),
									title: membership.title,
								})),
							]}
							selectedItemId={membershipId?.toString() ?? "none"}
							onChange={value => {
								const id = parseNumber(value)
								setMembershipId(id !== null ? id : undefined)
							}}
						/>
					</div>
				)}
				{platform?.enabledFeatures.includes("invite-to-course") &&
					courses.data !== undefined &&
					courses.data.length > 0 && (
						<div>
							<InputLabel>Assign to courses</InputLabel>
							{courses.data.map(course => (
								<Checkbox
									key={course.id}
									label={course.title}
									isChecked={courseIds.includes(course.id)}
									onChange={() => toggleStateArray(setCourseIds, course.id)}
								/>
							))}
						</div>
					)}
			</Content>
			<ModalButtons>
				<Button variant="secondary" onClick={onClose} isDisabled={isSubmitting}>
					Cancel
				</Button>
				<SubmitButton variant="primary" isDisabled={!isInputValid} isLoading={isSubmitting}>
					Send invites
				</SubmitButton>
			</ModalButtons>
		</DefaultModal>
	)
}

const Content = styled(ModalContentPadding)`
	display: flex;
	flex-direction: column;
	gap: 16px;
`

const List = styled.ol`
	margin-left: 1em;
`

export default InviteUsersModal
